<script lang="ts" setup>
const { template } = storeToRefs(useTemplateStore());
const { app, public: publicConfig } = useRuntimeConfig();
const { lg } = useBreakpointTailwind();

const ipxModifier = computed(() => {
  return `q_${Number(publicConfig.imageQuality ?? 60)}`;
});

const images = computed(() => {
  const isDevelopment = import.meta.dev;

  const images = [
    "/hero-mobile.webp",
    ...Array.from({ length: template.value.gallery.imageCount }, (_, i) => `/gallery-${i + 1}.webp`),
  ];

  if (isDevelopment) {
    return images;
  }

  return images.map((image) => {
    return `${app.baseURL}_ipx/${ipxModifier.value}${image}`;
  });
});

const galleryItemAspectClasses = [
  "aspect-[2/3]",
  "aspect-[4/5]",
  "aspect-[3/4]",
  "aspect-[3/5]",
  "aspect-[3/5]",
  "aspect-[2/3]",
] as const;

function getGalleryItemClass(index: number) {
  const aspectClass = galleryItemAspectClasses[index % galleryItemAspectClasses.length]
    ?? "aspect-[3/4]";

  if (index === 3 || index === 4) {
    return `${aspectClass} lg:aspect-[1/2]`;
  }

  return aspectClass;
}

const galleryItems = computed(() => {
  return images.value.map((src, index) => ({ index, src }));
});

const galleryColumns = computed(() => {
  const items = galleryItems.value;

  if (lg.value) {
    const firstColumnEnd = Math.ceil(items.length / 3);
    const secondColumnEnd = firstColumnEnd + Math.floor(items.length / 3);

    return [
      items.slice(0, firstColumnEnd),
      items.slice(firstColumnEnd, secondColumnEnd),
      items.slice(secondColumnEnd),
    ];
  }

  return [
    items.filter(item => item.index % 2 === 0),
    items.filter(item => item.index % 2 === 1),
  ];
});
</script>

<template>
  <section
    v-if="template.gallery.enabled"
    id="gallery"
    class="p-section relative h-safe-min-screen max-h-full flex justify-center overflow-hidden bg-primary-950 text-white text-center"
  >
    <div
      aria-hidden="true"
      class="gallery-texture pointer-events-none absolute inset-0"
    />

    <div class="relative z-1 mx-auto flex max-w-[34rem] flex-col gap-16 lg:max-w-5xl">
      <AppSectionHeaderWrapper>
        <AppSectionTitle
          data-aos="fade-up"
          data-aos-delay="180"
        >
          GALERI PENGANTIN
        </AppSectionTitle>

        <NuxtImg
          src="/line-white.svg"
          alt="Line White"
          class="w-line-graphic"
          data-aos="zoom-in"
          data-aos-delay="320"
          data-aos-duration="700"
          data-aos-anchor="#gallery"
          loading="lazy"
          decoding="async"
        />
      </AppSectionHeaderWrapper>

      <p
        class="text-sm mx-auto max-w-2xs xs:text-base xs:max-w-xs sm:max-w-sm xl:text-lg xl:max-w-md"
        data-aos="fade"
        data-aos-delay="750"
        data-aos-anchor="#gallery"
      >
        Momen-momen bahagia kami selama perjalanan cinta hingga hari pernikahan ini
      </p>

      <LazyAppFancybox
        class="w-full"
        :fancybox-options="{
          Carousel: {
            Zoomable: {
              Panzoom: {
                protected: true,
              },
            },
          },
        }"
      >
        <div class="grid grid-cols-2 items-start gap-4 lg:grid-cols-3 lg:gap-5">
          <div
            v-for="(column, columnIndex) in galleryColumns"
            :key="columnIndex"
            class="flex min-w-0 flex-col gap-4 lg:gap-5"
            :class="columnIndex === 1 ? 'pt-6 lg:pt-8' : ''"
          >
            <div
              v-for="item in column"
              :key="item.src"
              class="group relative"
              :class="getGalleryItemClass(item.index)"
            >
              <button
                type="button"
                data-fancybox="gallery"
                class="relative block size-full overflow-hidden rounded-[1.35rem] border border-white/12 bg-white/8 p-0 text-left shadow-[0_22px_60px_rgba(0,0,0,0.34)]"
                :data-src="item.src"
                :aria-label="`Buka foto galeri ${item.index + 1}`"
              >
                <NuxtImg
                  class="size-full object-cover transition duration-700 group-hover:scale-105"
                  decoding="async"
                  :src="`/${item.src.split('/').pop()}`"
                  :alt="`Gallery image ${item.index + 1}`"
                  :fetchpriority="item.index === 0 ? 'high' : 'auto'"
                  :loading="item.index === 0 ? 'eager' : 'lazy'"
                  :preload="item.index === 0 ? { fetchPriority: 'high' } : false"
                />
              </button>
            </div>
          </div>
        </div>
      </LazyAppFancybox>
    </div>
  </section>
</template>

<style lang="css" scoped>
.gallery-texture {
  background:
    radial-gradient(circle at 12% 8%, rgba(230, 216, 161, 0.18), transparent 30%),
    radial-gradient(circle at 88% 38%, rgba(174, 116, 49, 0.22), transparent 34%),
    radial-gradient(circle at 42% 92%, rgba(221, 200, 130, 0.12), transparent 32%),
    linear-gradient(180deg, rgba(119, 71, 41, 0.18), rgba(145, 88, 44, 0.2) 55%, rgba(197, 146, 59, 0.1));
}

.gallery-texture::after {
  position: absolute;
  inset: 0;
  background-image: repeating-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.025) 0,
    rgba(255, 255, 255, 0.025) 1px,
    transparent 1px,
    transparent 12px
  );
  content: "";
}
</style>
